/* AHRIN — Cart/PDP stabilization patch (keys-first, robust line detection) */
(function () {
  try { console.debug('[AHRIN] cart-patch loaded'); } catch (_) {}
  if (window.__ahrinCartPatch) return;
  window.__ahrinCartPatch = true;

  // Keep theme globals satisfied so nothing crashes
  window.routes = window.routes || {
    cart_change_url: '/cart/change.js',
    cart_update_url: '/cart/update.js',
    cart_add_url: '/cart/add.js',
    cart_url: '/cart',
    predictive_search_url: '/search/suggest'
  };

  /* ---------------- tiny utils ---------------- */
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (t, ev, fn, opts) => t.addEventListener(ev, fn, opts || false);

  async function fetchJSON(url, opts = {}) {
    const res  = await fetch(url, Object.assign({ credentials: 'same-origin' }, opts));
    const text = await res.text();
    let data = null;
    try { data = JSON.parse(text); } catch { data = text; }
    return { ok: res.ok, status: res.status, data, text };
  }

  // Canonical bubble refresher (with legacy alias support)
  async function refreshCountNow() {
    try {
      if (typeof window.refreshCartCount === 'function') {
        await window.refreshCartCount();
      } else if (typeof window.updateCartBubble === 'function') {
        await window.updateCartBubble();
      }
    } catch (_) {}
  }

  /* ---------------- cart DOM helpers ---------------- */
  function findRow(from) {
    return from.closest('[data-cart-item], [data-item], .cart-item, tr.cart-item, [data-cart-line]');
  }

  function findQtyInput(row) {
    return (
      row?.querySelector('input[name^="updates["]') ||    // updates[KEY] / updates[]
      row?.querySelector('input[name="updates[]"]') ||
      row?.querySelector('input[data-quantity-input]') ||
      row?.querySelector('input[type="number"]')
    );
  }

  // Extract the line-item key if it exists
  function getLineKey(row) {
    const byAttr =
      row?.getAttribute('data-cart-item-key') ||
      row?.dataset?.cartItemKey ||
      row?.querySelector('[data-cart-item-key]')?.getAttribute('data-cart-item-key') ||
      null;
    if (byAttr) return byAttr;

    const input = findQtyInput(row);
    const name = input?.getAttribute('name') || '';
    const m = name.match(/^updates\[(.+?)\]$/); // updates[KEY]
    return m ? m[1] : null;
  }

  // Robust line index when using name="updates[]"
  function getLineIndex(row) {
    const input = findQtyInput(row);
    if (!input) return 0;

    const name = input.getAttribute('name') || '';
    const form = input.form || document.querySelector('form[action="/cart"]') || document.querySelector('form[action*="/cart"]');
    if (!form) return 0;

    if (name === 'updates[]') {
      const list = Array.from(form.querySelectorAll('input[name="updates[]"]'));
      const idx = list.indexOf(input);
      return idx >= 0 ? (idx + 1) : 0; // 1-based
    }
    return 0;
  }

  function setRowBusy(row, busy) {
    if (!row) return;
    row.classList.toggle('is-busy', !!busy);
    $$(
      '[data-quantity-plus], [data-quantity-minus], .quantity__button, button[name="plus"], button[name="minus"], [data-cart-remove], [name="remove"], .cart-remove, .cart-item__remove',
      row
    ).forEach(b => { b.disabled = !!busy; });
  }

  /* ---------------- API with fallbacks ---------------- */
  async function changeQuantity(row, quantity) {
    const key  = getLineKey(row);             // preferred
    const line = key ? 0 : getLineIndex(row); // compute only when no key
    const qty  = Math.max(0, Number(quantity) || 0);

    // 1) change.js by key
    if (key) {
      const r = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (r.ok) { await refreshCountNow(); return true; }
      console.warn('[AHRIN] change(key) failed', r.status, r.data);
    }

    // 2) change.js by line (works with name="updates[]")
    if (!key && line > 0) {
      const r = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      });
      if (r.ok) { await refreshCountNow(); return true; }
      console.warn('[AHRIN] change(line) failed', r.status, r.data);
    }

    // 3) update.js fallback using updates map
    const updates = {};
    if (key) updates[key] = qty;
    else if (line > 0) updates[line] = qty;

    const rU = await fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (rU.ok) { await refreshCountNow(); return true; }

    console.error('[AHRIN] update.js failed', rU.status, rU.data);
    throw new Error('cart-update-failed');
  }

  async function adjust(row, delta) {
    if (!row || row.dataset.ahrinBusy === '1') return;
    row.dataset.ahrinBusy = '1'; setRowBusy(row, true);
    try {
      const input = findQtyInput(row);
      const cur = Number(input ? input.value : 1) || 1;
      const next = Math.max(0, cur + Number(delta || 0));
      await changeQuantity(row, next);
      // Reload to let theme redraw totals/lines safely
      location.reload();
    } catch (e) {
      console.error('[AHRIN] cart adjust failed', e);
      setRowBusy(row, false); row.dataset.ahrinBusy = '0';
      alert('Could not update cart. Please try again.');
    }
  }

  async function removeRow(row) {
    if (!row || row.dataset.ahrinBusy === '1') return;
    row.dataset.ahrinBusy = '1'; setRowBusy(row, true);
    try {
      await changeQuantity(row, 0);
      location.reload();
    } catch (e) {
      console.error('[AHRIN] cart remove failed', e);
      setRowBusy(row, false); row.dataset.ahrinBusy = '0';
      alert('Could not remove item. Please try again.');
    }
  }

  /* ---------------- preempt theme handlers (stop stuck spinners) ---------------- */
  function matchAction(target) {
    if (target.closest('[data-quantity-plus], .quantity__button[name="plus"], button[name="plus"], [aria-label="Increase quantity"]')) return 'plus';
    if (target.closest('[data-quantity-minus], .quantity__button[name="minus"], button[name="minus"], [aria-label="Reduce quantity"]')) return 'minus';
    if (target.closest('[data-cart-remove], [name="remove"], .cart-remove, .cart-item__remove, button[aria-label="Remove"], a[aria-label="Remove"]')) return 'remove';
    return '';
  }

  function hookCartPage() {
    // Intercept earliest to cancel theme spinner path
    on(document, 'pointerdown', (e) => {
      const act = matchAction(e.target);
      if (!act) return;
      const row = findRow(e.target);
      if (!row) return;
      e.preventDefault(); e.stopPropagation();
      row.dataset.__ahrinPending = act;
    }, { capture: true });

    // Perform action on click (still capture), so keyboard works too
    on(document, 'click', (e) => {
      const row = findRow(e.target);
      const act = row && row.dataset.__ahrinPending;
      if (!row || !act) return;
      e.preventDefault(); e.stopPropagation();
      delete row.dataset.__ahrinPending;

      if (act === 'plus')  return void adjust(row, +1);
      if (act === 'minus') return void adjust(row, -1);
      if (act === 'remove') return void removeRow(row);
    }, { capture: true });
  }

  /* ---- PDP: Intercept Add to cart form and refresh header bubble ---- */
  async function addFromForm(form) {
    if (form.__ahrinBusy) return;
    form.__ahrinBusy = true;

    const btn = form.querySelector('[type="submit"], button[name="add"], button') || null;
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

    try {
      // Build payload (keeps any extra form keys such as properties)
      const fd = new FormData(form);
      const payload = {};
      fd.forEach((v, k) => { payload[k] = v; });

      // Add to cart (JSON)
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('add-failed');

      // Ask the canonical refresher to repaint the header/drawer bubble
      await refreshCountNow();

      try { window.toast?.('Added to cart.'); } catch (_) {}
      return true;
    } catch (e) {
      console.error('[AHRIN] PDP add failed', e);
      try { window.toast?.('Could not add to cart. Please try again.'); } catch (_) {}
      return false;
    } finally {
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
      form.__ahrinBusy = false;
    }
  }

  function hookPDP() {
    document.addEventListener('submit', (e) => {
      const form = e.target?.closest('form[action^="/cart/add"], form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault(); e.stopPropagation();
      addFromForm(form);
    }, true);
  }

  /* ---------------- last-resort spinner killer ---------------- */
  (function hardenCartReload(){
    let lastCount = null;
    async function getCart() {
      try { const r = await fetch('/cart.js', { credentials: 'same-origin' }); return r.ok ? r.json() : null; }
      catch { return null; }
    }
    if (location.pathname === '/cart') {
      getCart().then(c => { lastCount = c ? c.item_count : null; });
      document.addEventListener('click', (e) => {
        const affected = e.target.closest(
          '[data-quantity-plus], [data-quantity-minus], .quantity__button, button[name="plus"], button[name="minus"], [data-cart-remove], [name="remove"], .cart-remove, .cart-item__remove'
        );
        if (!affected) return;
        setTimeout(async () => {
          if (document.visibilityState === 'hidden') return;
          const c = await getCart();
          if (!c || lastCount == null) return;
          if (c.item_count !== lastCount) location.reload();
        }, 1500);
      }, true);
    }
  })();

  /* ---------------- init ---------------- */
  function init(){ hookCartPage(); hookPDP(); }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
